import { useMemo } from 'react';
import { airAmbulanceAdapter } from './airAmbulanceAdapter';
import { towProAdapter } from './towProAdapter';
import type { ResourceRequestAdapter, ResourceRequestRow } from './types';

/**
 * Registered ETL resource request adapters. When you add one, list it here,
 * call its `useEnabled` and `useList` once inside
 * `useResourceRequestRegistryLists`, and include it in
 * `useEnabledResourceRequestAdapters`. Hooks for every adapter always run so
 * hook order stays stable; disabled adapters are filtered from the results.
 */
export const RESOURCE_REQUEST_ADAPTERS = [
  airAmbulanceAdapter,
  towProAdapter,
] as const;

export type RegisteredResourceRequestAdapter =
  (typeof RESOURCE_REQUEST_ADAPTERS)[number];

export type ResourceRequestRegistryListSlice = {
  adapterId: RegisteredResourceRequestAdapter['id'];
  rows: ResourceRequestRow[];
  isFetching: boolean;
};

/**
 * Creation timestamp for sorting, tolerant of both naming conventions: GMR
 * transports use snake_case `created_at`, digital dispatch requests use
 * camelCase `createdAt`.
 */
function requestCreatedAtIso(request: unknown): string | null {
  if (typeof request !== 'object' || request === null) {
    return null;
  }
  const record = request as { created_at?: unknown; createdAt?: unknown };
  const value = record.created_at ?? record.createdAt;
  return typeof value === 'string' ? value : null;
}

/**
 * Bind one request to its adapter as a type-erased row. Inside this generic
 * the adapter/request pairing is type-checked; consumers only ever see the
 * bound {@link ResourceRequestRow}.
 */
function buildRow<RequestData>(
  adapter: ResourceRequestAdapter<RequestData>,
  request: RequestData,
): ResourceRequestRow {
  const { ListItemComponent, BannerComponent, getQueueInfo } = adapter;
  const id = adapter.getId(request);

  return {
    key: `${adapter.id}-${id}`,
    id,
    adapterId: adapter.id,
    callerId: adapter.getCallerId(request),
    createdAtIso: requestCreatedAtIso(request),
    pickupLocation: adapter.getPickupLocation?.(request) ?? null,
    ingestionMetadata: adapter.getIngestionMetadata?.(request) ?? null,
    getQueueInfo: getQueueInfo ? nowMs => getQueueInfo(request, nowMs) : null,
    renderListItem: props => <ListItemComponent request={request} {...props} />,
    renderBanner: BannerComponent
      ? props => <BannerComponent request={request} {...props} />
      : null,
  };
}

/**
 * Bind an adapter's request list to rows. Exported for tests that mock the
 * registry hook with their own adapters.
 */
export function buildResourceRequestRows<RequestData>(
  adapter: ResourceRequestAdapter<RequestData>,
  requests: readonly RequestData[],
): ResourceRequestRow[] {
  return requests.map(request => buildRow(adapter, request));
}

/**
 * The registered adapters the current agency and user may use. Determines
 * which option cards render in the new-request view.
 */
export function useEnabledResourceRequestAdapters(): readonly RegisteredResourceRequestAdapter[] {
  const airAmbulanceEnabled = airAmbulanceAdapter.useEnabled();
  const towProEnabled = towProAdapter.useEnabled();

  return useMemo(() => {
    const adapters: RegisteredResourceRequestAdapter[] = [];
    if (airAmbulanceEnabled) {
      adapters.push(airAmbulanceAdapter);
    }
    if (towProEnabled) {
      adapters.push(towProAdapter);
    }
    return adapters;
  }, [airAmbulanceEnabled, towProEnabled]);
}

/**
 * Aggregates list polling across enabled adapters. Disabled adapters skip
 * their list query and contribute no slice.
 */
export function useResourceRequestRegistryLists(): readonly ResourceRequestRegistryListSlice[] {
  const airAmbulanceEnabled = airAmbulanceAdapter.useEnabled();
  const airAmbulanceList = airAmbulanceAdapter.useList({
    skip: !airAmbulanceEnabled,
  });
  const towProEnabled = towProAdapter.useEnabled();
  const towProList = towProAdapter.useList({ skip: !towProEnabled });

  return useMemo((): readonly ResourceRequestRegistryListSlice[] => {
    const slices: ResourceRequestRegistryListSlice[] = [];

    if (airAmbulanceEnabled) {
      slices.push({
        adapterId: airAmbulanceAdapter.id,
        rows: buildResourceRequestRows(
          airAmbulanceAdapter,
          airAmbulanceList.requests,
        ),
        isFetching: airAmbulanceList.isFetching,
      });
    }

    if (towProEnabled) {
      slices.push({
        adapterId: towProAdapter.id,
        rows: buildResourceRequestRows(towProAdapter, towProList.requests),
        isFetching: towProList.isFetching,
      });
    }

    return slices;
  }, [
    airAmbulanceEnabled,
    airAmbulanceList.requests,
    airAmbulanceList.isFetching,
    towProEnabled,
    towProList.requests,
    towProList.isFetching,
  ]);
}

export type {
  ResourceRequestAdapter,
  ResourceRequestBannerProps,
  ResourceRequestDetailView,
  ResourceRequestListItemProps,
  ResourceRequestPickupLocation,
  ResourceRequestQueueCategory,
  ResourceRequestRow,
} from './types';
export { airAmbulanceAdapter } from './airAmbulanceAdapter';
export { towProAdapter } from './towProAdapter';
